import { ChampsOrigine } from '../tracabilite/ChampsOrigine';
import { choixAgriculteur } from '../../lib/tracabilite';
import { useEffect, useState } from 'react';
import { X, Zap, Truck, UserPlus } from '../../lib/icons';
import { toast } from 'sonner';
import { ContactCombobox } from '../shared/ContactCombobox';
import { ContactQuickForm } from './ContactQuickForm';
import { fetchTiers, creerPeseeEclair, QUALITES } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const big =
  'mt-1.5 h-14 w-full border-2 border-slate-300 bg-slate-50 px-3 font-data text-2xl font-semibold text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const label = 'text-xs font-bold uppercase tracking-[0.1em] text-slate-500';

// Saisie éclair pont-bascule : contact + poids brut, validation en un clic.
export const PeseeEclairForm = ({ onClose, onCreated }) => {
  const [tiers, setTiers] = useState([]);
  const [f, setF] = useState({ tiersId: '', parcours: '', immatriculation: '', poidsBrut: '', tare: '', qualite: '' });
  useEffect(() => {
    if (!f.tiersId || !tiers.some(c=>c.id===f.tiersId)) return;
    setF(p=>p._traceTiers===p.tiersId?p:({...p,...choixAgriculteur(p.tiersId,tiers),_traceTiers:p.tiersId}));
  }, [f.tiersId, tiers]);
  const [enCours, setEnCours] = useState(false);
  const [nouveau, setNouveau] = useState(false);

  useEffect(() => {
    fetchTiers().then(setTiers);
  }, []);

  // Contact créé à la volée : code attribué, enregistré, puis sélectionné immédiatement.
  const contactCree = async (c) => {
    const rows = await fetchTiers();
    setTiers(rows);
    setNouveau(false);
    const auto = c.estFournisseur && !c.estClient ? 'fournisseur' : c.estClient && !c.estFournisseur ? 'client' : c.estFournisseur ? 'fournisseur' : 'client';
    setF((p) => ({ ...p, tiersId: c.id, parcours: auto }));
  };

  const contact = tiers.find((c) => c.id === f.tiersId);
  const roles = contact ? { client: contact.estClient === true, fournisseur: contact.estFournisseur === true } : null;
  const mixte = roles && roles.client && roles.fournisseur;

  const choisir = (id) => {
    const c = tiers.find((x) => x.id === id);
    const auto = c?.estFournisseur && !c?.estClient ? 'fournisseur' : c?.estClient && !c?.estFournisseur ? 'client' : '';
    setF((p) => ({ ...p, tiersId: id, parcours: auto || (c?.estFournisseur ? 'fournisseur' : 'client') }));
  };

  const valider = async (e) => {
    e.preventDefault();
    setEnCours(true);
    const { error, row } = await creerPeseeEclair(f);
    setEnCours(false);
    if (error) return toast.error(error);
    toast.success(
      row.statut === 'Sur site'
        ? `Ticket ${row.id} — camion libéré, en attente de tare (sortie)`
        : `Réception ${row.id} enregistrée — détails à compléter`
    );
    onCreated(row);
    onClose();
  };

  const net = f.tare !== '' ? Math.max(0, Number(f.poidsBrut) - Number(f.tare)) : null;

  return (
    <div data-testid="pesee-eclair-overlay" className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/60 p-4">
      <form onSubmit={valider} data-testid="pesee-eclair-form" className="flex max-h-[calc(100dvh-2rem)] min-h-0 w-full max-w-xl flex-col border border-slate-200 border-t-4 border-t-slate-900 bg-white">
        <div className="flex shrink-0 items-start justify-between border-b border-slate-200 px-5 py-4">
          <div>
            <h2 className="flex items-center gap-2 font-heading text-lg font-black tracking-tight text-slate-900">
              <Zap size={18} /> Pesée éclair — Entrée
            </h2>
            <p className="text-xs text-slate-500">Contact, qualité et poids brut. Prix, rendement et stockage se complètent ensuite.</p>
          </div>
          <button type="button" data-testid="pesee-eclair-close" onClick={onClose} className="border border-slate-200 p-2 text-slate-500 hover:bg-slate-900 hover:text-white">
            <X size={16} />
          </button>
        </div>
        <div data-testid="pesee-eclair-scroll" className="min-h-0 overflow-y-scroll overscroll-contain space-y-4 px-5 py-5" style={{ scrollbarGutter: 'stable' }}>
          <div><label className={label}>Immatriculation du véhicule</label>
            <input data-testid="eclair-immatriculation" maxLength={32} value={f.immatriculation} onChange={(e) => setF(p => ({...p, immatriculation: e.target.value}))} placeholder="Ex. 123 TU 4567" className={`${big} text-lg`} />
          </div>
          <ChampsOrigine value={f} onChange={setF} />
          <div>
            <label className={label}>Contact — nom, code (CT-xxxx) ou QR</label>
            <div className="flex items-start gap-2">
              <ContactCombobox contacts={tiers} value={f.tiersId} onChange={choisir} testid="eclair-tiers" placeholder="Nom, code contact, téléphone…" className="flex-1" />
              <button
                type="button"
                data-testid="eclair-nouveau-contact"
                onClick={() => setNouveau(true)}
                title="Créer un nouveau contact"
                className="mt-1.5 flex h-11 flex-shrink-0 items-center gap-1.5 border-2 border-slate-900 px-3 font-heading text-xs font-bold uppercase tracking-[0.06em] text-slate-900 transition-colors duration-150 hover:bg-slate-900 hover:text-white"
              >
                <UserPlus size={15} /> Nouveau
              </button>
            </div>
            {contact && (
              <p data-testid="eclair-contact-info" className="mt-1.5 font-data text-[11px] text-slate-600">
                {contact.code} · {contact.nom} {contact.tel ? `· ${contact.tel}` : ''}
              </p>
            )}
          </div>
          {contact && (
            <div>
              <label className={label}>Type d'opération {mixte ? '(contact mixte : choisissez)' : '(déduit du contact)'}</label>
              <div className="mt-1.5 grid grid-cols-2 gap-px bg-slate-200">
                {[['fournisseur', 'Achat d’olives (fournisseur)'], ['client', 'Trituration (client)']].map(([id, lib]) => (
                  <button
                    key={id}
                    type="button"
                    data-testid={`eclair-parcours-${id}`}
                    onClick={() => setF((p) => ({ ...p, parcours: id }))}
                    className={`px-3 py-2.5 text-left text-sm font-bold transition-colors duration-150 ${f.parcours === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                  >
                    {lib}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <label className={label}>Qualité des olives (obligatoire)</label>
            <div className="mt-1.5 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-3">
              {QUALITES.map((q) => {
                const active = f.qualite === q.id;
                const lamp = q.famille === 'lampante';
                return (
                  <button
                    key={q.id}
                    type="button"
                    data-testid={`eclair-qualite-${q.id}`}
                    onClick={() => setF((p) => ({ ...p, qualite: q.id }))}
                    className={`px-3 py-2.5 text-sm font-bold transition-colors duration-150 ${
                      active ? (lamp ? 'bg-orange-600 text-white' : 'bg-slate-900 text-white') : 'bg-white text-slate-700 hover:bg-slate-50'
                    }`}
                  >
                    {q.label}
                  </button>
                );
              })}
            </div>
            <p className="mt-1.5 text-[11px] text-slate-500">
              Sans qualité, le bon ne peut pas être routé vers sa file d’atelier — saisie exigée dès l’entrée.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="sm:col-span-2">
              <label className={label}>Poids brut (Kg) — camion chargé</label>
              <input data-testid="eclair-poids-brut" type="number" min="0" inputMode="numeric" autoFocus={false} value={f.poidsBrut} onChange={(e) => setF((p) => ({ ...p, poidsBrut: e.target.value }))} className={big} />
            </div>
            <div>
              <label className={label}>Tare (optionnel)</label>
              <input data-testid="eclair-tare" type="number" min="0" inputMode="numeric" placeholder="à la sortie" value={f.tare} onChange={(e) => setF((p) => ({ ...p, tare: e.target.value }))} className={`${big} text-lg`} />
            </div>
          </div>
          {net !== null && (
            <p data-testid="eclair-net" className="font-data text-sm text-slate-700">Poids net : <b>{fmt(net)} Kg</b></p>
          )}
        </div>
        <div className="flex shrink-0 gap-3 border-t border-slate-200 px-5 py-4">
          <button
            type="submit"
            data-testid="eclair-valider"
            disabled={enCours || !f.tiersId || !f.parcours || !f.qualite || !Number(f.poidsBrut)}
            className="flex h-14 flex-1 items-center justify-center gap-2 bg-slate-900 font-heading text-base font-bold uppercase tracking-[0.08em] text-white transition-colors duration-150 hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            <Truck size={18} /> {f.tare !== '' ? 'Enregistrer la réception' : 'Valider & libérer le camion'}
          </button>
          <button type="button" data-testid="eclair-annuler" onClick={onClose} className="h-14 border border-slate-300 px-5 text-sm font-semibold text-slate-700 hover:bg-slate-100">
            Annuler
          </button>
        </div>
      </form>
      {nouveau && (
        <div className="relative z-[70]">
          <ContactQuickForm typeInitial="tiers" onClose={() => setNouveau(false)} onCreated={contactCree} />
        </div>
      )}
    </div>
  );
};
